import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Shield,
  Trash2,
  AlertTriangle,
  Clock,
  Users,
  Car,
  FileText,
  DollarSign,
  Wrench,
  ChevronDown,
  Check,
  X,
  History,
  Database
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Sorting function for alphanumeric vehicle IDs (01, 01.1, 02, 02.1, etc.)
const sortAlphanumeric = (a, b) => {
  const parseId = (id) => {
    if (!id) return [0, 0];
    const parts = String(id).split('.');
    const main = parseInt(parts[0].replace(/\D/g, '')) || 0;
    const sub = parts[1] ? parseInt(parts[1]) : 0;
    return [main, sub];
  };
  
  const [aMain, aSub] = parseId(a);
  const [bMain, bSub] = parseId(b);
  
  if (aMain !== bMain) return aMain - bMain;
  return aSub - bSub;
};

export default function AdminPage() {
  const { API, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('contracts');
  
  // Data for each module
  const [contracts, setContracts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [stats, setStats] = useState({});
  
  // Selection state
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
    loadStats();
    loadAuditLogs();
  }, [activeTab]);

  useEffect(() => {
    setSelectedItems([]);
    setSelectAll(false);
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'contracts':
          const contractsRes = await axios.get(`${API}/contracts`, { withCredentials: true });
          setContracts(contractsRes.data);
          break;
        case 'vehicles':
          const vehiclesRes = await axios.get(`${API}/vehicles`, { withCredentials: true });
          // Sort by alphanumeric ID
          const sortedVehicles = vehiclesRes.data.sort((a, b) => 
            sortAlphanumeric(a.vehicle_id, b.vehicle_id)
          );
          setVehicles(sortedVehicles);
          break;
        case 'clients':
          const clientsRes = await axios.get(`${API}/clients`, { withCredentials: true });
          setClients(clientsRes.data);
          break;
        case 'invoices':
          // limit explícito de propósito: aqui "selecionar todos" serve para exclusão em lote,
          // então não carregamos as 20 mil faturas de uma vez
          const invoicesRes = await axios.get(`${API}/invoices`, { params: { limit: 1000 }, withCredentials: true });
          setInvoices(invoicesRes.data);
          break;
        case 'transactions':
          const transRes = await axios.get(`${API}/transactions`, { withCredentials: true });
          setTransactions(transRes.data);
          break;
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const res = await axios.get(`${API}/admin/audit-logs?limit=50`, { withCredentials: true });
      setAuditLogs(res.data);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'contracts': return contracts;
      case 'vehicles': return vehicles;
      case 'clients': return clients;
      case 'invoices': return invoices;
      case 'transactions': return transactions;
      default: return [];
    }
  };

  const getIdField = () => {
    switch (activeTab) {
      case 'contracts': return 'contract_id';
      case 'vehicles': return 'vehicle_id';
      case 'clients': return 'client_id';
      case 'invoices': return 'invoice_id';
      case 'transactions': return 'transaction_id';
      default: return 'id';
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      const idField = getIdField();
      setSelectedItems(getCurrentData().map(item => item[idField]));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectItem = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(i => i !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleBulkDelete = async () => {
    if (confirmPhrase !== 'APAGAR DADOS DO SISTEMA') {
      toast.error('Frase de confirmação incorreta');
      return;
    }

    setDeleting(true);
    try {
      const res = await axios.post(`${API}/admin/bulk-delete`, {
        module: activeTab,
        record_ids: selectedItems,
        confirmation_phrase: confirmPhrase
      }, { withCredentials: true });

      toast.success(`${res.data.deleted_count} registro(s) excluído(s)`);
      setShowDeleteModal(false);
      setConfirmPhrase('');
      setSelectedItems([]);
      setSelectAll(false);
      loadData();
      loadStats();
      loadAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir registros');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800">Acesso Restrito</h2>
          <p className="text-gray-500 mt-2">Esta área é exclusiva para administradores.</p>
        </div>
      </div>
    );
  }

  const tabConfig = [
    { key: 'contracts', label: 'Contratos', icon: FileText, count: stats.contracts },
    { key: 'vehicles', label: 'Veículos', icon: Car, count: stats.vehicles },
    { key: 'clients', label: 'Clientes', icon: Users, count: stats.clients },
    { key: 'invoices', label: 'Faturas', icon: DollarSign, count: stats.invoices },
    { key: 'transactions', label: 'Financeiro', icon: DollarSign, count: stats.transactions },
  ];

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 font-[Manrope] flex items-center gap-2">
            <Shield className="w-6 h-6 text-red-600" />
            Painel Administrativo
          </h1>
          <p className="text-gray-500 text-sm">Gerenciamento e limpeza de dados em massa</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {tabConfig.map(tab => {
          const Icon = tab.icon;
          return (
            <Card key={tab.key} className="bg-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">{tab.label}</p>
                    <p className="text-2xl font-bold text-gray-800">{tab.count || 0}</p>
                  </div>
                  <Icon className="w-8 h-8 text-gray-300" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100">
          {tabConfig.map(tab => {
            const Icon = tab.icon;
            return (
              <TabsTrigger key={tab.key} value={tab.key} className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                {tab.label}
              </TabsTrigger>
            );
          })}
          <TabsTrigger value="audit" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Logs de Auditoria
          </TabsTrigger>
        </TabsList>

        {/* Action Bar */}
        {activeTab !== 'audit' && (
          <div className="flex items-center justify-between mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                className="flex items-center gap-2"
              >
                {selectAll ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                {selectAll ? 'Desmarcar Todos' : 'Selecionar Todos'}
              </Button>
              <span className="text-sm text-gray-600">
                {selectedItems.length} selecionado(s)
              </span>
            </div>
            
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeleteModal(true)}
              disabled={selectedItems.length === 0}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4" />
              Excluir Selecionados
            </Button>
          </div>
        )}

        {/* Contracts Tab */}
        <TabsContent value="contracts" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-12">
                        <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} />
                      </TableHead>
                      <TableHead>ID</TableHead>
                      <TableHead>Veículo</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Criado em</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contracts.map(item => (
                      <TableRow key={item.contract_id} className={selectedItems.includes(item.contract_id) ? 'bg-red-50' : ''}>
                        <TableCell>
                          <Checkbox 
                            checked={selectedItems.includes(item.contract_id)}
                            onCheckedChange={() => handleSelectItem(item.contract_id)}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{item.contract_id}</TableCell>
                        <TableCell>{item.vehicle_id}</TableCell>
                        <TableCell>{item.client_name}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            item.status === 'ativo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {item.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">{formatDate(item.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vehicles Tab */}
        <TabsContent value="vehicles" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-12">
                        <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} />
                      </TableHead>
                      <TableHead>ID Veículo</TableHead>
                      <TableHead>Placa</TableHead>
                      <TableHead>Marca/Modelo</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Valor FIPE</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vehicles.map(item => (
                      <TableRow key={item.vehicle_id} className={selectedItems.includes(item.vehicle_id) ? 'bg-red-50' : ''}>
                        <TableCell>
                          <Checkbox 
                            checked={selectedItems.includes(item.vehicle_id)}
                            onCheckedChange={() => handleSelectItem(item.vehicle_id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{item.vehicle_id}</TableCell>
                        <TableCell>{item.placa}</TableCell>
                        <TableCell>{item.marca} {item.modelo}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            item.status === 'disponivel' ? 'bg-green-100 text-green-700' :
                            item.status === 'alugado' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {item.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(item.valor_fipe)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-12">
                        <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} />
                      </TableHead>
                      <TableHead>ID</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>CPF/CNPJ</TableHead>
                      <TableHead>Telefone</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map(item => (
                      <TableRow key={item.client_id} className={selectedItems.includes(item.client_id) ? 'bg-red-50' : ''}>
                        <TableCell>
                          <Checkbox 
                            checked={selectedItems.includes(item.client_id)}
                            onCheckedChange={() => handleSelectItem(item.client_id)}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{item.client_id}</TableCell>
                        <TableCell className="font-medium">{item.nome}</TableCell>
                        <TableCell>{item.cpf_cnpj}</TableCell>
                        <TableCell>{item.telefone}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            item.status === 'ativo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {item.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-12">
                        <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} />
                      </TableHead>
                      <TableHead>ID</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Vencimento</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.slice(0, 100).map(item => (
                      <TableRow key={item.invoice_id} className={selectedItems.includes(item.invoice_id) ? 'bg-red-50' : ''}>
                        <TableCell>
                          <Checkbox 
                            checked={selectedItems.includes(item.invoice_id)}
                            onCheckedChange={() => handleSelectItem(item.invoice_id)}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{item.invoice_id}</TableCell>
                        <TableCell>{item.client_name}</TableCell>
                        <TableCell>{item.vencimento}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            item.status === 'paga' ? 'bg-green-100 text-green-700' :
                            item.status === 'vencida' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {item.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(item.valor_original)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-12">
                        <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} />
                      </TableHead>
                      <TableHead>ID</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.slice(0, 100).map(item => (
                      <TableRow key={item.transaction_id} className={selectedItems.includes(item.transaction_id) ? 'bg-red-50' : ''}>
                        <TableCell>
                          <Checkbox 
                            checked={selectedItems.includes(item.transaction_id)}
                            onCheckedChange={() => handleSelectItem(item.transaction_id)}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{item.transaction_id}</TableCell>
                        <TableCell className="max-w-xs truncate">{item.descricao}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            item.tipo === 'receita' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {item.tipo}
                          </span>
                        </TableCell>
                        <TableCell>{item.data}</TableCell>
                        <TableCell className={`text-right font-medium ${item.tipo === 'receita' ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(item.valor)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Logs de Auditoria
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Módulo</TableHead>
                    <TableHead>Registros</TableHead>
                    <TableHead>IP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        Nenhum log de auditoria encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    auditLogs.map(log => (
                      <TableRow key={log.log_id}>
                        <TableCell className="text-sm">{formatDate(log.created_at)}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{log.user_name}</p>
                            <p className="text-xs text-gray-500">{log.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                            {log.action}
                          </span>
                        </TableCell>
                        <TableCell>{log.module}</TableCell>
                        <TableCell className="font-bold text-red-600">{log.records_affected}</TableCell>
                        <TableCell className="font-mono text-xs text-gray-500">{log.ip_address || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-6 h-6" />
              ATENÇÃO: Exclusão Permanente
            </DialogTitle>
            <DialogDescription className="text-gray-700">
              Você está prestes a excluir <strong className="text-red-600">{selectedItems.length}</strong> registro(s) do módulo <strong>{activeTab}</strong>.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
            <p className="text-red-800 text-sm font-medium mb-2">
              Esta ação é IRREVERSÍVEL e não pode ser desfeita.
            </p>
            <p className="text-red-700 text-sm">
              Para confirmar, digite a frase abaixo:
            </p>
            <p className="text-red-900 font-bold text-center my-2 p-2 bg-red-100 rounded">
              APAGAR DADOS DO SISTEMA
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirm-phrase">Digite a frase de confirmação:</Label>
            <Input
              id="confirm-phrase"
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              placeholder="Digite aqui..."
              className="border-red-300 focus:border-red-500"
              data-testid="confirm-phrase-input"
            />
          </div>
          
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setConfirmPhrase('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={confirmPhrase !== 'APAGAR DADOS DO SISTEMA' || deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Confirmar Exclusão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
